import { CalendarMonthOutlined } from '@mui/icons-material'
import { Box, Paper, ThemeProvider, Tooltip, ToggleButton, ToggleButtonGroup, Typography, createTheme } from '@mui/material'
import React from 'react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import { RANGE_OPTIONS } from '../../hooks/useDateRange'

const pickerTheme = createTheme({ palette: { mode: 'dark', primary: { main: '#f2674a' } } })

interface DateRangeFilterProps {
  range: string
  onRangeChange: (event: React.MouseEvent<HTMLElement>, value: string | null) => void
  customStart: Dayjs | null
  onCustomStartChange: (value: Dayjs | null) => void
  customEnd: Dayjs | null
  onCustomEndChange: (value: Dayjs | null) => void
  accent?: string
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  range, onRangeChange, customStart, onCustomStartChange, customEnd, onCustomEndChange, accent = '#f2674a',
}) => {
  const datePickerSx = {
    width: 150,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '0.85rem',
      color: '#fff',
      '& fieldset': { borderColor: `${accent}66` },
      '&:hover fieldset': { borderColor: accent },
      '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiInputLabel-root': { color: '#898989' },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper
        elevation={0}
        className="bg-dark-color"
        sx={{
          p: 2, borderRadius: '10px', border: `2px solid ${accent}55`,
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box className="color-primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
          <CalendarMonthOutlined sx={{ fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Date Range</Typography>
        </Box>

        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={onRangeChange}
          sx={{
            gap: 1,
            '& .MuiToggleButton-root': {
              border: `1px solid ${accent}55`,
              borderRadius: '8px !important',
              color: '#b0b0b0',
              fontWeight: 600,
              fontSize: '0.8rem',
              textTransform: 'none',
              px: 2, py: 0.6,
              transition: 'all 0.2s ease',
              '&:hover': { backgroundColor: `${accent}1A`, borderColor: accent },
              '&.Mui-selected': {
                backgroundColor: accent,
                color: '#fff',
                boxShadow: `0 4px 14px ${accent}66`,
                '&:hover': { backgroundColor: accent },
              },
            },
          }}
        >
          {RANGE_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value}>
              <Tooltip title={opt.full} arrow>
                <span>{opt.label}</span>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {range === 'custom' && (
          <ThemeProvider theme={pickerTheme}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: { sm: 'auto' }, flexWrap: 'wrap' }}>
              <DatePicker
                label="From"
                value={customStart}
                onChange={onCustomStartChange}
                maxDate={customEnd || dayjs()}
                slotProps={{ textField: { size: 'small', sx: datePickerSx } }}
                slots={{ openPickerIcon: (props) => <CalendarMonthOutlined {...props} className="color-primary" /> }}
              />
              <Typography sx={{ color: '#898989' }}>–</Typography>
              <DatePicker
                label="To"
                value={customEnd}
                onChange={onCustomEndChange}
                minDate={customStart || undefined}
                maxDate={dayjs()}
                slotProps={{ textField: { size: 'small', sx: datePickerSx } }}
                slots={{ openPickerIcon: (props) => <CalendarMonthOutlined {...props} className="color-primary" /> }}
              />
            </Box>
          </ThemeProvider>
        )}
      </Paper>
    </LocalizationProvider>
  )
}

export default DateRangeFilter